import { Big } from "big.js";
import { ZERO } from "./bigUtil.ts";
import type { CostType } from "./CaseConfiguration.ts";
import type { Case, CostApportionment } from "./CaseModel.ts";
import type { CostItem, CostPart, Costs } from "./CostModel.ts";

/** An item's parts: towards each opponent, then the generic one. */
export const partsOf = ({ towards, generic }: CostItem): CostPart[] => [
  ...Object.values(towards),
  generic,
];

const sumOf = (parts: CostPart[], of: keyof CostPart): Big =>
  parts.reduce((sum, part) => sum.add(part[of]), ZERO);

/** What of an item is incurred. */
export const incurredOf = (item: CostItem): Big =>
  sumOf(partsOf(item), "incurred");

/** What of an item is still estimated. */
export const estimatedOf = (item: CostItem): Big =>
  sumOf(partsOf(item), "estimated");

/** An item's amount: what of it is incurred and what estimated. */
export const amountOf = (item: CostItem): Big =>
  incurredOf(item).add(estimatedOf(item));

const part = (incurred: Big, estimated: Big): CostPart => ({
  incurred: incurred.toNumber(),
  estimated: estimated.toNumber(),
});

/**
 * The parts of a cost, `incurred` and `estimated` of it, by a party's
 * apportionment: towards each opponent its share, the rest generic; all of it
 * generic without an apportionment.
 */
export function partsBy(
  incurred: Big,
  estimated: Big,
  apportionment: CostApportionment | undefined,
): Pick<CostItem, "towards" | "generic"> {
  const towards: CostItem["towards"] = {};
  let rest = new Big(1);
  for (const [opponentId, share] of Object.entries(
    apportionment?.perParty ?? {},
  )) {
    rest = rest.minus(share);
    if (share > 0)
      towards[opponentId] = part(incurred.mul(share), estimated.mul(share));
  }
  const generic = rest.gt(0) ? rest : ZERO;
  return {
    towards,
    generic: part(incurred.mul(generic), estimated.mul(generic)),
  };
}

/**
 * A cost item of `amount`, the share `incurred` of it incurred, split by a
 * party's apportionment (see partsBy).
 */
export function costItem(
  id: string,
  type: CostType,
  amount: Big | number,
  incurred: Big | number = 0,
  apportionment?: CostApportionment,
  note?: string,
): CostItem {
  const total = new Big(amount);
  const ofIt = total.mul(incurred);
  return {
    id,
    type,
    ...(note !== undefined ? { note } : {}),
    ...partsBy(ofIt, total.minus(ofIt), apportionment),
  };
}

/** The item split anew by `apportionment`, what of it is incurred and estimated kept. */
export const splitBy = (
  item: CostItem,
  apportionment: CostApportionment | undefined,
): CostItem => ({
  ...item,
  ...partsBy(incurredOf(item), estimatedOf(item), apportionment),
});

/** The item with every part incurred by `share`, the rest of it estimated. */
export function withIncurred(item: CostItem, share: number): CostItem {
  const incurredBy = ({ incurred, estimated }: CostPart): CostPart => {
    const amount = new Big(incurred).add(estimated);
    const ofIt = amount.mul(share);
    return part(ofIt, amount.minus(ofIt));
  };
  return {
    ...item,
    towards: Object.fromEntries(
      Object.entries(item.towards).map(([id, of]) => [id, incurredBy(of)]),
    ),
    generic: incurredBy(item.generic),
  };
}

/** The item with every part scaled by `factor`. */
export function scaled(item: CostItem, factor: Big): CostItem {
  const scaledPart = ({ incurred, estimated }: CostPart): CostPart =>
    part(factor.mul(incurred), factor.mul(estimated));
  return {
    ...item,
    towards: Object.fromEntries(
      Object.entries(item.towards).map(([id, of]) => [id, scaledPart(of)]),
    ),
    generic: scaledPart(item.generic),
  };
}

/**
 * A cost as stored before items had parts: an amount, a fraction of it
 * incurred, in the oldest cases true or false for all or nothing.
 */
type StoredFee = {
  readonly id: string;
  type: CostType;
  amount: number;
  incurred?: number | boolean;
  note?: string;
};

/** Hours at a rate, as stored before hourly costs went. */
type StoredHourlyCosts = {
  type?: CostType;
  ratePerHour: number;
  entries?: {
    readonly id: string;
    hours: number;
    incurred?: number | boolean;
    note?: string;
  }[];
};

/** A stage's costs as stored, in whichever shape. */
type StoredCosts = Omit<Costs, "flatFees"> & {
  flatFees?: (CostItem | StoredFee)[];
  hourlyCosts?: StoredHourlyCosts[];
};

const isStoredFee = (item: CostItem | StoredFee): item is StoredFee =>
  "amount" in item;

const isStored = (costs: Costs): boolean => {
  const { flatFees, hourlyCosts } = costs as StoredCosts;
  return (
    hourlyCosts !== undefined ||
    flatFees === undefined ||
    flatFees.some(isStoredFee)
  );
};

/** True while a party's costs of the case are stored as amounts or hours. */
export function needsCostItems(c: Pick<Case, "budgets">): boolean {
  return (c.budgets ?? []).some(({ costs }) => costs.some(isStored));
}

/**
 * A case as the current code reads it: each cost stored as an amount, a
 * fraction of it incurred, and each entry of hours at a rate, as an item
 * split by its party's apportionment, in memory here as every save stores
 * it. What a share towards a party no longer in the case would get goes with
 * the party.
 */
export function withCostItems<C extends Pick<Case, "budgets" | "parties">>(
  c: C,
): C {
  if (!needsCostItems(c)) return c;
  const inCase = new Set((c.parties ?? []).map(({ id }) => id));
  return {
    ...c,
    budgets: c.budgets?.map((budget) => {
      const apportionment = c.parties?.find(
        ({ id }) => id === budget.partyId,
      )?.apportionment;
      const itemOf = (
        { id, type, note }: { id: string; type: CostType; note?: string },
        amount: Big,
        fraction: number | boolean = 0,
      ): CostItem => {
        const item = costItem(
          id,
          type,
          amount,
          Number(fraction),
          apportionment,
          note,
        );
        return {
          ...item,
          towards: Object.fromEntries(
            Object.entries(item.towards).filter(([partyId]) =>
              inCase.has(partyId),
            ),
          ),
        };
      };
      return {
        ...budget,
        costs: budget.costs.map((costs): Costs => {
          if (!isStored(costs)) return costs;
          const { flatFees, hourlyCosts, ...stage } = costs as StoredCosts;
          return {
            ...stage,
            flatFees: [
              ...(flatFees ?? []).map((item) =>
                isStoredFee(item)
                  ? itemOf(item, new Big(item.amount), item.incurred)
                  : item,
              ),
              ...(hourlyCosts ?? []).flatMap(({ type, ratePerHour, entries }) =>
                (entries ?? []).map((entry) =>
                  itemOf(
                    { ...entry, type: type || "lawFirmFee" },
                    new Big(entry.hours).mul(ratePerHour),
                    entry.incurred,
                  ),
                ),
              ),
            ],
          };
        }),
      };
    }),
  };
}
